import { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

export interface DropdownOption {
  value: string
  title: string
}

/**
 * Checks if an object is found in another array of objects.
 */
export function hasObject(records?: object[], values?: object) {
  if (!records) {
    return false
  }

  return records.some((record) => {
    for (const key in record) {
      if (
        values &&
        key in values &&
        (record as Record<string, unknown>)[key] !=
          (values as Record<string, unknown>)[key]
      ) {
        return false
      }
    }

    return true
  })
}

/**
 * Keeps a number within a range.
 */
export function clampRange(value: number, min = 0, max = 1) {
  return value < min ? min : value > max ? max : value
}

/**
 * Parses JSON string into an object.
 */
export const parseJson = (json: string): Record<string, unknown> => {
  try {
    return JSON.parse(json)
  } catch {
    return {}
  }
}

/**
 * Compares numbers for sorting.
 */
export const compareNumbers = (number1: number, number2: number) =>
  number1 > number2 ? 1 : number1 < number2 ? -1 : 0

/**
 * Compares strings for sorting.
 */
export const compareStrings = (string1: string, string2: string) =>
  string1.localeCompare(string2)

/**
 * Determines if 2 variables are equal using JSON representation.
 */
export const isEqual = (variable1: unknown, variable2: unknown) =>
  JSON.stringify(variable1) === JSON.stringify(variable2)

/**
 * Generates all combinations from multiple arrays.
 * E.g., getAllCombinations(['a', 'b'], ['1', '2']) returns [['a', '1'], ['a', '2'], ['b', '1'], ['b', '2']].
 */
export const getAllCombinations = (...arrays: string[][]): string[][] => {
  const initialValue: string[][] = [[]]

  return [...arrays].reduce(
    (resultArrays, array) =>
      resultArrays
        .map((resultArray) =>
          array.map((arrayValue) => resultArray.concat(arrayValue))
        )
        .reduce(
          (newResultArrays, arraysItem) => newResultArrays.concat(arraysItem),
          []
        ),
    initialValue
  )
}

/**
 * Generates a random string based on UUID.
 */
export const getRandomString = () => Buffer.from(uuidv4()).toString('base64')

/**
 * Gets published and draft document IDs from given ID.
 */
export const getDocumentIds = (id: string) => {
  const publishedId = id.replace(/^drafts\./, '')
  const draftId = `drafts.${publishedId}`

  return [publishedId, draftId] as const
}

/**
 * Detects touch device.
 */
export const useIsTouchDevice = () => {
  const [isTouchDevice, setIsTouchDevice] = useState(false)

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0)
  }, [])

  return isTouchDevice
}

/**
 * Escapes a string to be using in regular expressions.
 * "$&" means the whole matched string.
 */
export const escapeRegExp = (string: string) =>
  string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Determines if JavaScript object is numeric.
 */
export const isNumeric = (object: string) => {
  const normalizedObject =
    typeof object === 'string' ? object.replace(/,/g, '') : object
  const parsedObject = parseFloat(normalizedObject)

  return (
    !isNaN(parsedObject) &&
    isFinite(parsedObject) &&
    Object.prototype.toString.call(normalizedObject).toLowerCase() !==
      '[object array]'
  )
}

/**
 * Converts a string into a hash.
 */
export const stringToHash = (string: string) => {
  let hash = 0
  let chr: number

  if (string.length === 0) {
    return hash
  }

  for (let i = 0; i < string.length; i += 1) {
    chr = string.charCodeAt(i)
    hash = (hash << 5) - hash + chr
    hash |= 0
  }

  return hash
}

/**
 * Recursively replaces values in any data structure.
 */
const recursiveUpdateValue = (
  value: unknown,
  setter: (value: unknown, currentKey?: string, fullKey?: string) => unknown,
  currentKey?: string,
  fullKey?: string
): unknown => {
  // Use setter to potentially update value
  const updatedValue = setter(value, currentKey, fullKey)

  // If value is an array, iterate over its items
  if (Array.isArray(updatedValue)) {
    return (updatedValue as unknown[])
      .map((item, index) => {
        const childKey = `${index}`
        const childFullKey = fullKey ? `${fullKey}.${childKey}` : childKey

        return recursiveUpdateValue(item, setter, childKey, childFullKey)
      })
      .filter((item) => typeof item !== 'undefined')
  }

  // If value is an object, iterate over its keys
  if (updatedValue && typeof updatedValue === 'object') {
    const entries = Object.entries(updatedValue)
    const updatedEntries = entries
      .map(([childKey, childValue]) => {
        const childFullKey = fullKey ? `${fullKey}.${childKey}` : childKey

        return [
          childKey,
          recursiveUpdateValue(childValue, setter, childKey, childFullKey),
        ]
      })
      .filter(([, childValue]) => typeof childValue !== 'undefined')

    return Object.fromEntries(updatedEntries)
  }

  // If value is a primitive, return updated value
  return updatedValue
}

/**
 * Recursively replaces null values with undefined.
 */
export const getNonNullValues = <T>(value: T): T => {
  return recursiveUpdateValue(value, (value) => {
    if (value === null) {
      return
    }

    return value
  }) as T
}
